import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, XCircle, DollarSign, AlertTriangle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { supabase } from '../../lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SaaSSubscription {
  id: string;
  company_id: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string;
  plan_type: 'basic' | 'pro' | 'enterprise';
  status: string;
  billing_cycle: 'monthly' | 'annual';
  price: number;
  billing_end_date: string | null;
  cancel_at_period_end: boolean;
  cancellation_requested_at: string | null;
  cancellation_effective_date: string | null;
  companies: { id: string; name: string };
}

interface PaymentRecord {
  id: string;
  stripe_payment_intent_id: string | null;
  amount: number;
  status: string;
  payment_type: string;
  billing_date: string | null;
  paid_at: string | null;
  refunded_at: string | null;
}

type ConfirmDialog =
  | { type: 'cancel-period-end'; sub: SaaSSubscription }
  | { type: 'cancel-immediate'; sub: SaaSSubscription }
  | { type: 'refund-full'; payment: PaymentRecord }
  | { type: 'refund-partial'; payment: PaymentRecord; amountDollars: string }
  | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-600',
  trialing: 'bg-cyan-600',
  past_due: 'bg-yellow-600',
  unpaid: 'bg-red-600',
  cancelled: 'bg-[#504A4A]',
  incomplete: 'bg-orange-600',
  paused: 'bg-blue-600',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'bg-[#504A4A]';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${color}`}>
      {status}
    </span>
  );
}

function fmt(date: string | null) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SubscriptionManagementPage() {
  const { adminUser, isLoading: authLoading } = useAdminAuth();

  const [subscriptions, setSubscriptions] = useState<SaaSSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const [confirm, setConfirm] = useState<ConfirmDialog>(null);
  const [partialAmount, setPartialAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const selectedSub = subscriptions.find(s => s.id === selectedId) ?? null;

  // ── Data loading ─────────────────────────────────────────────────────────

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('saas_subscriptions')
      .select(`
        id, company_id, stripe_subscription_id, stripe_customer_id,
        plan_type, status, billing_cycle, price, billing_end_date,
        cancel_at_period_end, cancellation_requested_at, cancellation_effective_date,
        companies!inner(id, name)
      `)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSubscriptions(data as unknown as SaaSSubscription[]);
    }
    setLoading(false);
  }, []);

  const loadPayments = useCallback(async (subscriptionId: string) => {
    setPaymentsLoading(true);
    const { data, error } = await supabase
      .from('payment_records')
      .select('id, stripe_payment_intent_id, amount, status, payment_type, billing_date, paid_at, refunded_at')
      .eq('saas_subscription_id', subscriptionId)
      .order('billing_date', { ascending: false });

    if (!error && data) setPayments(data as unknown as PaymentRecord[]);
    setPaymentsLoading(false);
  }, []);

  useEffect(() => { loadSubscriptions(); }, [loadSubscriptions]);

  useEffect(() => {
    if (selectedId) loadPayments(selectedId);
    else setPayments([]);
  }, [selectedId, loadPayments]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Edge function call ────────────────────────────────────────────────────

  const callEdgeFn = useCallback(async (body: Record<string, unknown>) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-subscription`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    );
    const json = await res.json();
    if (!json.success) throw new Error(json.error ?? 'Request failed');
    return json;
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────

  const runConfirmedAction = useCallback(async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      if (confirm.type === 'cancel-period-end') {
        await callEdgeFn({ action: 'cancel', subscription_id: confirm.sub.id, mode: 'period_end' });
        setToast({ type: 'success', message: `${confirm.sub.companies.name} will be cancelled at period end.` });
      } else if (confirm.type === 'cancel-immediate') {
        await callEdgeFn({ action: 'cancel', subscription_id: confirm.sub.id, mode: 'immediate' });
        setToast({ type: 'success', message: `${confirm.sub.companies.name} cancelled immediately.` });
      } else if (confirm.type === 'refund-full') {
        const result = await callEdgeFn({ action: 'refund', payment_record_id: confirm.payment.id });
        setToast({ type: 'success', message: `Full refund issued. Stripe refund ID: ${result.refund_id}` });
        if (selectedId) await loadPayments(selectedId);
      } else if (confirm.type === 'refund-partial') {
        const parsedAmount = parseFloat(confirm.amountDollars);
        if (!Number.isFinite(parsedAmount) || parsedAmount <= 0 || parsedAmount > confirm.payment.amount) {
          throw new Error(`Partial refund amount must be greater than $0 and no more than $${confirm.payment.amount.toFixed(2)}.`);
        }
        const cents = Math.round(parsedAmount * 100);
        const result = await callEdgeFn({ action: 'refund', payment_record_id: confirm.payment.id, amount_cents: cents });
        setToast({ type: 'success', message: `Partial refund of $${confirm.amountDollars} issued. Stripe refund ID: ${result.refund_id}` });
        if (selectedId) await loadPayments(selectedId);
      }
      await loadSubscriptions();
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : 'Action failed' });
    } finally {
      setActionLoading(false);
      setConfirm(null);
      setPartialAmount('');
    }
  }, [confirm, callEdgeFn, loadSubscriptions, loadPayments, selectedId]);

  const handleRefresh = useCallback(async (sub: SaaSSubscription) => {
    setActionLoading(true);
    try {
      const result = await callEdgeFn({ action: 'refresh', subscription_id: sub.id });
      setToast({ type: 'success', message: `Synced from Stripe — status: ${result.stripe_status}` });
      await loadSubscriptions();
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : 'Refresh failed' });
    } finally {
      setActionLoading(false);
    }
  }, [callEdgeFn, loadSubscriptions]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1421]" />
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-red-500">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        } text-white`}>
          <span className="text-sm font-medium">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 hover:opacity-70">&times;</button>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#EBFFEE]">Subscription Management</h1>
        <p className="text-sm text-[#A9A3A3] mt-1">Cancel subscriptions and issue refunds without leaving RollOps.</p>
      </div>

      {/* Subscription list */}
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-[#A9A3A3]">SaaS Subscriptions</CardTitle>
            <Button
              variant="outline"
              onClick={loadSubscriptions}
              disabled={loading}
              className="text-xs border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232]"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              <span className="ml-1.5">Reload</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#9B1421]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#504A4A]">
                    {['Company', 'Plan', 'Status', 'Price', 'Period End', 'Stripe Sub ID', ''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#A9A3A3]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(sub => (
                    <tr
                      key={sub.id}
                      className={`border-b border-[#363232] cursor-pointer transition-colors ${
                        selectedId === sub.id ? 'bg-[#2a2626]' : 'hover:bg-[#2a2626]'
                      }`}
                      onClick={() => setSelectedId(selectedId === sub.id ? null : sub.id)}
                    >
                      <td className="px-4 py-3 font-medium text-[#EBFFEE]">{sub.companies.name}</td>
                      <td className="px-4 py-3 text-[#A9A3A3] capitalize">{sub.plan_type} / {sub.billing_cycle}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={sub.status} />
                        {sub.cancel_at_period_end && (
                          <span className="ml-1.5 text-xs text-yellow-400">cancels at period end</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#A9A3A3]">${sub.price}/mo</td>
                      <td className="px-4 py-3 text-[#A9A3A3]">{fmt(sub.billing_end_date)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#A9A3A3]">
                        {sub.stripe_subscription_id ?? <span className="text-red-400">none</span>}
                      </td>
                      <td className="px-4 py-3 text-[#A9A3A3]">
                        {selectedId === sub.id
                          ? <ChevronUp className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />}
                      </td>
                    </tr>
                  ))}
                  {subscriptions.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-[#A9A3A3]">No subscriptions found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail panel */}
      {selectedSub && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Actions */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-[#A9A3A3]">
                Actions — {selectedSub.companies.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* IDs */}
              <div className="space-y-1 text-xs text-[#A9A3A3] font-mono bg-[#1a1818] rounded p-3">
                <div><span className="text-[#504A4A]">Stripe sub:</span> {selectedSub.stripe_subscription_id ?? '—'}</div>
                <div><span className="text-[#504A4A]">Stripe customer:</span> {selectedSub.stripe_customer_id ?? '—'}</div>
                {selectedSub.cancellation_requested_at && (
                  <div><span className="text-yellow-500">Cancellation requested:</span> {fmt(selectedSub.cancellation_requested_at)}</div>
                )}
                {selectedSub.cancellation_effective_date && (
                  <div><span className="text-red-400">Effective:</span> {fmt(selectedSub.cancellation_effective_date)}</div>
                )}
              </div>

              {/* Cancel actions */}
              {selectedSub.status !== 'cancelled' && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-[#A9A3A3]">Cancellation</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      disabled={actionLoading || selectedSub.cancel_at_period_end}
                      onClick={() => setConfirm({ type: 'cancel-period-end', sub: selectedSub })}
                      className="text-xs border-yellow-600 text-yellow-400 hover:bg-yellow-600/10 disabled:opacity-40"
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5" />
                      Cancel at Period End
                    </Button>
                    <Button
                      variant="outline"
                      disabled={actionLoading}
                      onClick={() => setConfirm({ type: 'cancel-immediate', sub: selectedSub })}
                      className="text-xs border-red-600 text-red-400 hover:bg-red-600/10"
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5" />
                      Cancel Immediately
                    </Button>
                  </div>
                </div>
              )}

              {/* Refresh */}
              {selectedSub.stripe_subscription_id && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-[#A9A3A3]">State Sync</p>
                  <Button
                    variant="outline"
                    disabled={actionLoading}
                    onClick={() => handleRefresh(selectedSub)}
                    className="text-xs border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232]"
                  >
                    {actionLoading
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Refresh from Stripe
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment history */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-[#A9A3A3]">Payment History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {paymentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-[#9B1421]" />
                </div>
              ) : payments.length === 0 ? (
                <p className="px-4 py-6 text-sm text-[#A9A3A3]">No payment records found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#504A4A]">
                        {['Date', 'Amount', 'Status', ''].map(h => (
                          <th key={h} className="text-left px-4 py-2 text-xs font-medium text-[#A9A3A3]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id} className="border-b border-[#363232]">
                          <td className="px-4 py-2.5 text-[#A9A3A3]">{fmt(p.billing_date ?? p.paid_at)}</td>
                          <td className="px-4 py-2.5 text-[#EBFFEE]">${p.amount.toFixed(2)}</td>
                          <td className="px-4 py-2.5">
                            <StatusBadge status={p.status} />
                            {p.refunded_at && (
                              <span className="ml-1.5 text-xs text-[#A9A3A3]">{fmt(p.refunded_at)}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            {p.status === 'succeeded' && p.stripe_payment_intent_id && (
                              <div className="flex gap-1.5">
                                <button
                                  disabled={actionLoading}
                                  onClick={() => setConfirm({ type: 'refund-full', payment: p })}
                                  className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40"
                                >
                                  Full refund
                                </button>
                                <span className="text-[#504A4A]">·</span>
                                <button
                                  disabled={actionLoading}
                                  onClick={() => {
                                    setPartialAmount('');
                                    setConfirm({ type: 'refund-partial', payment: p, amountDollars: '' });
                                  }}
                                  className="text-xs text-yellow-400 hover:text-yellow-300 disabled:opacity-40"
                                >
                                  Partial
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 w-full max-w-md mx-4 space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
              <h2 className="text-base font-semibold text-[#EBFFEE]">
                {confirm.type === 'cancel-period-end' && 'Cancel at Period End'}
                {confirm.type === 'cancel-immediate' && 'Cancel Immediately'}
                {confirm.type === 'refund-full' && 'Issue Full Refund'}
                {confirm.type === 'refund-partial' && 'Issue Partial Refund'}
              </h2>
            </div>

            <div className="text-sm text-[#A9A3A3] space-y-1">
              {confirm.type === 'cancel-period-end' && (
                <>
                  <p>Subscription for <strong className="text-[#EBFFEE]">{confirm.sub.companies.name}</strong> will be cancelled at the end of the current billing period.</p>
                  <p className="font-mono text-xs mt-2">{confirm.sub.stripe_subscription_id}</p>
                </>
              )}
              {confirm.type === 'cancel-immediate' && (
                <>
                  <p><strong className="text-red-400">This will stop billing immediately.</strong> The subscription for <strong className="text-[#EBFFEE]">{confirm.sub.companies.name}</strong> will be cancelled right now with no proration.</p>
                  <p className="font-mono text-xs mt-2">{confirm.sub.stripe_subscription_id}</p>
                </>
              )}
              {confirm.type === 'refund-full' && (
                <p>Issue a full refund of <strong className="text-[#EBFFEE]">${confirm.payment.amount.toFixed(2)}</strong> to the customer.</p>
              )}
              {confirm.type === 'refund-partial' && (
                <div className="space-y-2">
                  <p>Enter the refund amount (max ${confirm.payment.amount.toFixed(2)}):</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[#EBFFEE]">$</span>
                    <input
                      type="number"
                      min="0.01"
                      max={confirm.payment.amount}
                      step="0.01"
                      value={partialAmount}
                      onChange={e => {
                        setPartialAmount(e.target.value);
                        setConfirm({ ...confirm, amountDollars: e.target.value });
                      }}
                      className="flex-1 bg-[#363232] border border-[#504A4A] rounded px-3 py-1.5 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => { setConfirm(null); setPartialAmount(''); }}
                disabled={actionLoading}
                className="border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232]"
              >
                Cancel
              </Button>
              <Button
                onClick={runConfirmedAction}
                disabled={
                  actionLoading ||
                  (confirm.type === 'refund-partial' && (!partialAmount || parseFloat(partialAmount) <= 0))
                }
                className={`${
                  confirm.type.startsWith('cancel') ? 'bg-red-700 hover:bg-red-800' : 'bg-[#9B1421] hover:bg-[#7A0F1A]'
                } text-white disabled:opacity-50`}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
